import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import TalentInfoHeader from '../Utils/TalentInfoHeader';
import ProfileMetricCard from '../CandidateComponents/Shards/ProfileMetricCard';
import AdditionalMetricCard from '../CandidateComponents/Shards/AdditionalMetricCard';
import StrengthsConcernsCard from '../CandidateComponents/Shards/StrengthsConcernsCard';
import HardSkillsSection from '../CandidateComponents/Shards/HardSkillsSection';
import SoftSkillsSection from '../CandidateComponents/Shards/SoftSkillsSection';
import AreasOfExpertise from '../CandidateComponents/Shards/AreasOfExpertise';

const PrintableTalentProfileAssessment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    assessmentData, 
    talentProfileJSON,
    roleCategory
  } = location.state || {};

  // Log the data to help with debugging
  console.log('PrintableTalentProfileAssessment received:', { 
    assessmentData, 
    talentProfileJSON, 
    roleCategory 
  });

  // Extract data from talentProfileJSON if available
  const name = talentProfileJSON ? (talentProfileJSON.firstname + ' ' + talentProfileJSON.lastname || 'N/A') : 'N/A';
  const roleCategoryText = roleCategory?.mainCategory || 'N/A';
  const role = roleCategory?.subCategory || 'N/A';
  const totalYearsOfExperience = talentProfileJSON?.totalYearsOfExperience?.years || 'N/A';
  
  // Extract data from assessmentData if available
  const seniority = assessmentData?.seniority || {};
  const summary = assessmentData?.summary || {};
  const generalAbility = assessmentData?.generalAbility || {};
  const aiProficiency = assessmentData?.["AI Proficiency"] || {};
  const securityAwareness = assessmentData?.["Security Awareness"] || {};
  const multidisciplinaryExpertise = assessmentData?.["Multidisciplinary Expertise"] || {};
  const strengths = assessmentData?.strengths || {};
  const concerns = assessmentData?.concerns || {};
  const hardSkills = assessmentData?.hardSkills || {};
  const softSkills = assessmentData?.softSkills || {};
  const expertise = assessmentData?.expertise || {};

  return (
    <div className="p-8 max-w-5xl mx-auto bg-white">
      {/* Back button - only visible on screen, not when printing */}
      <button 
        onClick={() => navigate(-1)} 
        className="mb-6 flex items-center text-blue-600 hover:text-blue-800 print:hidden"
      >
        <FaArrowLeft className="mr-2" /> Back to Talent Profile Assessment
      </button>

      {/* Page title - only visible on screen, not when printing */}
      <h1 className="text-3xl font-bold mb-8 text-center print:hidden">Printable Talent Profile Assessment</h1>
      
      {/* Print instructions - only visible on screen, not when printing */}
      <div className="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-lg print:hidden">
        <p className="text-blue-800">
          This page is optimized for printing. Use Cmd+P (Mac) or Ctrl+P (Windows) to print or save as PDF.
          The header and this message will not appear in the printed version.
        </p>
      </div>

      {/* Report title - visible in print */}
      <h1 className="text-3xl font-bold mb-6 text-center hidden print:block">Talent Profile Assessment</h1>
      
      {/* Header with talent info */}
      <TalentInfoHeader
        name={name}
        roleCategory={roleCategoryText}
        role={role}
        yearsOfExperience={totalYearsOfExperience}
        seniority={seniority}
        showRationale={true}
      />
      
      {!assessmentData ? (
        <div className="p-4 bg-gray-50 rounded-lg">
          <p className="text-gray-600">Talent Profile Assessment data will be displayed here.</p>
        </div>
      ) : (
        <>
          {/* Summary Section */}
          <div className="print-break-inside-avoid">
            <div className="border border-blue-200 rounded-lg p-6 bg-blue-50 relative mb-6">
              <div className="flex justify-between items-start mb-3">
                <h4 className="text-lg font-semibold text-gray-800">Summary</h4>
                {summary?.source && (
                  <span className="text-xs text-gray-500">Source: {summary.source}</span>
                )}
              </div>
              <div className="h-full">
                <p className="text-gray-800 text-lg">{summary?.value || "No summary available."}</p>
              </div>
            </div>
            
            {/* General Ability Chart - now under Summary */}
            <ProfileMetricCard
              title="General Ability"
              value={generalAbility?.grade || 0}
              rationale={generalAbility?.rationale}
              source={generalAbility?.source}
            />
          </div>
          
          {/* Additional Metrics Section */}
          <div className="mt-6 print-break-inside-avoid">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h4 className="text-xl font-semibold text-gray-800 mb-4">Additional Key Metrics</h4>
              <div className="space-y-4">
                {/* AI Proficiency */}
                <AdditionalMetricCard 
                  title="AI Proficiency"
                  value={aiProficiency?.grade || 0}
                  rationale={aiProficiency?.rationale}
                  source={aiProficiency?.source}
                />
                
                {/* Security Awareness */}
                <AdditionalMetricCard 
                  title="Security Awareness"
                  value={securityAwareness?.grade || 0}
                  rationale={securityAwareness?.rationale}
                  source={securityAwareness?.source}
                />
                
                {/* Multidisciplinary Expertise */}
                <AdditionalMetricCard 
                  title="Multidisciplinary Expertise"
                  value={multidisciplinaryExpertise?.grade || 0}
                  rationale={multidisciplinaryExpertise?.rationale}
                  source={multidisciplinaryExpertise?.source}
                />
              </div>
            </div>
          </div>
          
          {/* Strengths and Concerns Section */}
          <div className="mt-6 print-break-inside-avoid">
            <StrengthsConcernsCard 
              strengths={strengths}
              concerns={concerns}
            />
          </div>
          
          {/* Hard Skills Section */}
          <div className="mt-6 print-break-inside-avoid">
            <HardSkillsSection 
              hardSkills={hardSkills} 
              radarChartMode="all" 
            />
          </div>
          
          {/* Soft Skills Section */}
          <div className="mt-6 print-break-inside-avoid">
            <SoftSkillsSection softSkills={softSkills} />
          </div>
          
          {/* Areas of Expertise Section */}
          <div className="mt-6 print-break-inside-avoid">
            <AreasOfExpertise expertise={expertise} />
          </div>
        </>
      )}
      
      {/* Footer with page number - only visible when printing */}
      <div className="mt-8 text-center text-gray-500 text-sm hidden print:block">
        <p>Generated on {new Date().toLocaleDateString()}</p>
      </div>
      
      {/* Add print-specific CSS */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @media print {
            @page {
              size: A4;
              margin: 0.8cm;
            }
            body {
              font-size: 12pt;
              color: #000;
              background-color: #fff;
            }
            .print-break-inside-avoid {
              break-inside: avoid;
            }
            .print-break-before {
              break-before: page;
            }
            /* Force flex-row layout in print view */
            .flex-col.md\\:flex-row {
              flex-direction: row !important;
            }
            /* Ensure left column maintains width in print view */
            .md\\:w-1\\/3 {
              width: 33.333333% !important;
              min-width: 200px !important;
              flex-shrink: 0 !important;
            }
            /* Ensure right column takes remaining width in print view */
            .md\\:w-2\\/3 {
              width: 66.666667% !important;
              padding-left: 20px !important;
            }
          }
        `
      }} />
    </div>
  );
};

export default PrintableTalentProfileAssessment;
